import Apris from '@assets/fonts/AprisVariableVF.woff2';
import LabGrotesqueBold from '@assets/fonts/LabGrotesque-Bold.woff2';
import LabGrotesqueLight from '@assets/fonts/LabGrotesque-Light.woff2';
import LabGrotesqueMedium from '@assets/fonts/LabGrotesque-Medium.woff2';
import LabGrotesqueRegular from '@assets/fonts/LabGrotesque-Regular.woff2';
import { alpha, responsiveFontSizes } from '@mui/material';
import { createTheme, Theme } from '@mui/material/styles';

export const getTheme = (theme: Theme): Theme => {
  let baseTheme = createTheme(theme, {
    customGradients: {
      primary: `linear-gradient(to right, ${theme.palette.gradientStart} 0%, ${theme.palette.gradientEnd} 100%)`,
      secondary: `linear-gradient(to right, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    },
    palette: {
      text: {
        primary: '#0B0B0A',
        secondary: '#2C2C2C',
      },
    },
    scrollbar: {
      color: `${alpha(theme.palette.secondary.dark, 0.5)} ${theme.palette.secondary.main}`,
    },
    shape: {
      borderRadius: '11px',
    },
    typography: {
      allVariants: {
        color: theme.palette.text.primary,
      },
      fontFamily: {
        header: 'Apris, LabGrotesque, Roboto, Helvetica, Arial',
        body: 'LabGrotesque, Roboto, Helvetica, Arial',
      },
      h1: {
        fontFamily: 'Apris, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
      },
      h2: {
        fontFamily: 'Apris, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
      },
      h3: {
        fontFamily: 'Apris, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
      },
      h4: {
        fontFamily: 'Apris, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
      },
      h5: {
        fontFamily: 'Apris, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
      },
      h6: {
        fontFamily: 'Apris, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
      },
      h7: {
        fontFamily: 'Apris, Roboto, Helvetica, Arial, sans-serif',
        fontSize: '1rem', // Adjust to your preferred size (smaller than h6's 1.25rem)
        fontWeight: 600, // Same as h6
        lineHeight: 1.6,
        letterSpacing: '0.0075em',
      },
      subtitle1: {
        fontFamily: 'LabGrotesque, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
      },
      subtitle2: {
        fontFamily: 'LabGrotesque, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
      },
      body1: {
        fontFamily: 'LabGrotesque, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
      },
      body2: {
        fontFamily: 'LabGrotesque, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
        @font-face {
          font-family: 'Apris';
          font-style: normal;
          font-display: swap;
          font-weight: 100 900;
          src: url(${Apris}) format('woff2');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
         @font-face {
          font-family: 'LabGrotesque';
          font-style: normal;
          font-display: swap;
          font-weight: 100;
          src: url(${LabGrotesqueLight}) format('woff');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
          @font-face {
          font-family: 'LabGrotesque';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: url(${LabGrotesqueRegular}) format('woff2');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
        @font-face {
          font-family: 'LabGrotesque';
          font-style: normal;
          font-display: swap;
          font-weight: 600;
          src: url(${LabGrotesqueMedium}) format('woff2');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
           @font-face {
          font-family: 'LabGrotesque';
          font-style: normal;
          font-display: swap;
          font-weight: Bold;
          src: url(${LabGrotesqueBold}) format('woff2');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
      `,
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            color: '#000000',
            boxShadow: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            '&.MuiButton-containedPrimary:not(.Mui-disabled)': {
              background: `linear-gradient(to right, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            },
            '&.MuiButton-containedSecondary:not(.Mui-disabled)': {
              background: `linear-gradient(to right, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
            },
            '&.MuiButton-outlinedPrimary': {
              color: theme.palette.primary.dark,
            },
            '&.pill': {
              borderRadius: '50px',
              padding: '8px 16px',
            },
            '&.button-normal-case': {
              textTransform: 'none',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: theme.palette.secondary.main,
            ['& svg']: {
              fill: theme.palette.secondary.main,
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          tag: {
            '&.MuiChip-root': {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.common.white,
              fontFamily: 'Arial',
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
              },
              '& svg': {
                color: theme.palette.common.white,
              },
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          root: {
            '& .MuiPaper-root': {
              padding: theme.spacing(2),
              color: theme.palette.text.secondary,
              borderRadius: '0.5rem',
            },
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            '&.Mui-selected, &.Mui-selected:focus': {
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          },
        },
      },
      // For year/month selection
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            '&.Mui-selected, &.Mui-selected:focus': {
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          },
        },
      },
      // For month selection
      MuiPickersMonth: {
        styleOverrides: {
          monthButton: {
            '&.Mui-selected, &.Mui-selected:focus': {
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          },
        },
      },
    },
  });
  baseTheme = responsiveFontSizes(baseTheme);
  return baseTheme;
};
